import { doc, updateDoc, increment, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserAccount } from '../types/user';

export class UsageTrackingService {
  /**
   * Initialize usage metrics for new user (DEPRECATED trial system removed)
   */
  static async initializeTrial(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      'usageMetrics.totalAgentViews': 0,
      'usageMetrics.agentViewsByType': {},
      'usageMetrics.portfolioUploads': 0,
      'usageMetrics.portfolioViewCount': 0,
      'usageMetrics.loginCount': 1,
      'usageMetrics.weeklyActiveStatus': true,
      'usageMetrics.monthlyActiveStatus': true,
      'usageMetrics.engagementScore': 0,
      'usageMetrics.lastLoginDate': serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Update trial status (DEPRECATED - subscription system removed)
   */
  static async updateTrialStatus(_userId: string): Promise<void> {
    // Trial system removed - this is a no-op for backwards compatibility
    return;
  }

  /**
   * Track agent page view
   */
  static async trackAgentView(userId: string, agentType: 'jerry' | 'xavier' | 'apollo' | 'kelly' | 'charles' | 'bradford' | 'nikki'): Promise<void> {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      'usageMetrics.totalAgentViews': increment(1),
      [`usageMetrics.agentViewsByType.${agentType}`]: increment(1),
      'usageMetrics.lastAgentView': serverTimestamp(),
      'usageMetrics.weeklyActiveStatus': true,
      'usageMetrics.monthlyActiveStatus': true,
      updatedAt: serverTimestamp()
    });

    // Recalculate engagement score
    await this.calculateEngagementScore(userId);
  }

  /**
   * Track portfolio upload
   */
  static async trackPortfolioUpload(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      'usageMetrics.portfolioUploads': increment(1),
      'usageMetrics.lastPortfolioUpload': serverTimestamp(),
      'usageMetrics.weeklyActiveStatus': true,
      'usageMetrics.monthlyActiveStatus': true,
      updatedAt: serverTimestamp()
    });

    // Recalculate engagement score
    await this.calculateEngagementScore(userId);
  }

  /**
   * Track portfolio view (Bradford dashboard)
   */
  static async trackPortfolioView(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      'usageMetrics.portfolioViewCount': increment(1),
      'usageMetrics.weeklyActiveStatus': true,
      'usageMetrics.monthlyActiveStatus': true,
      updatedAt: serverTimestamp()
    });

    // Recalculate engagement score
    await this.calculateEngagementScore(userId);
  }

  /**
   * Track user login
   */
  static async trackLogin(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      'usageMetrics.loginCount': increment(1),
      'usageMetrics.lastLoginDate': serverTimestamp(),
      'usageMetrics.weeklyActiveStatus': true,
      'usageMetrics.monthlyActiveStatus': true,
      updatedAt: serverTimestamp()
    });

    // Trial system removed - no longer needed
  }

  /**
   * Calculate engagement score (0-100)
   * Based on: agent views, portfolio uploads, recency, frequency
   */
  static async calculateEngagementScore(userId: string): Promise<number> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return 0;

    const userData = userDoc.data() as UserAccount;
    const metrics = userData.usageMetrics;

    if (!metrics) return 0;

    let score = 0;

    // Agent views (max 40 points)
    const agentViews = metrics.totalAgentViews || 0;
    score += Math.min(40, agentViews * 2); // 2 points per view, max 40

    // Portfolio uploads (max 25 points)
    const portfolioUploads = metrics.portfolioUploads || 0;
    score += Math.min(25, portfolioUploads * 5); // 5 points per upload, max 25

    // Diversity of agent usage (max 15 points)
    const agentTypes = metrics.agentViewsByType || {};
    const uniqueAgents = Object.keys(agentTypes).length;
    score += Math.min(15, uniqueAgents * 3); // 3 points per unique agent, max 15

    // Recency (max 10 points)
    const lastActivity = metrics.lastAgentView || metrics.lastLoginDate;
    if (lastActivity) {
      const daysSinceActivity = Math.floor((Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceActivity < 1) score += 10;
      else if (daysSinceActivity < 3) score += 7;
      else if (daysSinceActivity < 7) score += 5;
      else if (daysSinceActivity < 14) score += 3;
      else if (daysSinceActivity < 30) score += 1;
    }

    // Login frequency (max 10 points)
    const loginCount = metrics.loginCount || 0;
    score += Math.min(10, Math.floor(loginCount / 2)); // 0.5 points per login, max 10

    // Cap at 100
    score = Math.min(100, score);

    // Update Firestore
    await updateDoc(userRef, {
      'usageMetrics.engagementScore': score,
      updatedAt: serverTimestamp()
    });

    return score;
  }

  /**
   * Check if user's trial is active (DEPRECATED - always returns false)
   */
  static async isTrialActive(_userId: string): Promise<boolean> {
    // Trial system removed
    return false;
  }

  /**
   * Get user engagement summary for admin dashboard
   */
  static async getUserEngagementSummary(userId: string): Promise<{
    engagementScore: number;
    totalAgentViews: number;
    portfolioUploads: number;
    weeklyActive: boolean;
    monthlyActive: boolean;
    topAgents: Array<{ agent: string; views: number }>;
  }> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return {
        engagementScore: 0,
        totalAgentViews: 0,
        portfolioUploads: 0,
        weeklyActive: false,
        monthlyActive: false,
        topAgents: []
      };
    }

    const userData = userDoc.data() as UserAccount;
    const metrics = userData.usageMetrics || {
      totalAgentViews: 0,
      agentViewsByType: {},
      portfolioUploads: 0,
      portfolioViewCount: 0,
      loginCount: 0,
      weeklyActiveStatus: false,
      monthlyActiveStatus: false,
      engagementScore: 0
    };

    // Sort agents by view count
    const agentViewsByType = metrics.agentViewsByType || {};
    const topAgents = Object.entries(agentViewsByType)
      .map(([agent, views]) => ({ agent, views: views as number }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 3);

    return {
      engagementScore: metrics.engagementScore || 0,
      totalAgentViews: metrics.totalAgentViews || 0,
      portfolioUploads: metrics.portfolioUploads || 0,
      weeklyActive: metrics.weeklyActiveStatus || false,
      monthlyActive: metrics.monthlyActiveStatus || false,
      topAgents
    };
  }

  /**
   * Update weekly/monthly active status (call this daily via Cloud Function)
   */
  static async updateActiveStatus(userId: string): Promise<void> {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return;

    const userData = userDoc.data() as UserAccount;
    const metrics = userData.usageMetrics;

    if (!metrics) return;

    const now = new Date();
    const lastActivity = metrics.lastAgentView || metrics.lastLoginDate;

    if (!lastActivity) {
      await updateDoc(userRef, {
        'usageMetrics.weeklyActiveStatus': false,
        'usageMetrics.monthlyActiveStatus': false,
        updatedAt: serverTimestamp()
      });
      return;
    }

    const daysSinceActivity = Math.floor((now.getTime() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24));

    await updateDoc(userRef, {
      'usageMetrics.weeklyActiveStatus': daysSinceActivity < 7,
      'usageMetrics.monthlyActiveStatus': daysSinceActivity < 30,
      updatedAt: serverTimestamp()
    });
  }
}

export default UsageTrackingService;
